/**
 * Centralized storage cleanup utility for logout operations
 * This ensures all application data is properly cleared when user logs out
 */

// Import specific cache clearing functions
import { clearClientsCache } from '@/home/application/adapters/getClients';

/**
 * Storage keys used by the application
 */
const STORAGE_KEYS = {
  // Client-related keys
  SELECTED_CLIENT: 'selectedClient', // This is now preserved across logout
  CACHED_CLIENTS: 'cachedClients',
  CACHED_CLIENTS_TIMESTAMP: 'cachedClientsTimestamp',

  // Auth-related keys (now stored in localStorage)
  TOKEN: 'token',
  LOGIN_DATA: 'loginData',

  // Additional keys (if any are added later)
  // USER_PREFERENCES: 'userPreferences',
  // THEME_SETTINGS: 'themeSettings',

  // Payment-related keys (if any are added later)
  // PAYMENT_METHODS: 'paymentMethods',
  // BILLING_INFO: 'billingInfo',
} as const;

/**
 * Keys that should be preserved across logout/login sessions
 */
const PRESERVED_KEYS = [
  STORAGE_KEYS.SELECTED_CLIENT, // Keep selected client for next login
  // Add other keys here that should persist across sessions
] as const;

/**
 * Clear all sessionStorage data
 */
export const clearSessionStorage = (): void => {
  try {
    // Clear specific keys first (more controlled)
    Object.values(STORAGE_KEYS).forEach((key) => {
      sessionStorage.removeItem(key);
    });

    // Optional: Clear all sessionStorage (more aggressive approach)
    // sessionStorage.clear();

    console.log('SessionStorage cleared successfully');
  } catch (error) {
    console.warn('Failed to clear sessionStorage:', error);
  }
};

/**
 * Clear specific localStorage items (not all, to preserve non-user specific data)
 * Note: SELECTED_CLIENT is now preserved across logout/login
 */
export const clearUserLocalStorage = (): void => {
  try {
    // Clear client-related cache using existing function
    clearClientsCache();

    // Clear other user-specific localStorage items (excluding preserved keys)
    const userSpecificKeys = [
      STORAGE_KEYS.TOKEN, // Authentication token
      STORAGE_KEYS.LOGIN_DATA, // Login data
      // SELECTED_CLIENT is intentionally excluded - it should persist across logout
      // Add other user-specific keys here as needed
    ];

    userSpecificKeys.forEach((key) => {
      localStorage.removeItem(key);
    });

    console.log('User-specific localStorage cleared successfully (selected client preserved)');
  } catch (error) {
    console.warn('Failed to clear user localStorage:', error);
  }
};

/**
 * Complete storage cleanup for logout
 * Clears all user-related data from both sessionStorage and localStorage
 */
export const clearAllUserStorage = (): void => {
  console.log('Starting complete storage cleanup for logout...');

  try {
    // Clear all sessionStorage
    clearSessionStorage();

    // Clear user-specific localStorage
    clearUserLocalStorage();

    console.log('✅ Complete storage cleanup completed successfully');
  } catch (error) {
    console.error('❌ Error during storage cleanup:', error);
  }
};

/**
 * Get summary of what will be cleared (useful for debugging)
 */
export const getStorageCleanupSummary = (): {
  sessionStorageKeys: string[];
  localStorageKeys: string[];
  preservedKeys: string[];
} => {
  const sessionStorageKeys = Object.values(STORAGE_KEYS);
  const localStorageKeys = [
    STORAGE_KEYS.CACHED_CLIENTS,
    STORAGE_KEYS.CACHED_CLIENTS_TIMESTAMP,
    STORAGE_KEYS.TOKEN,
    STORAGE_KEYS.LOGIN_DATA,
  ];
  const preservedKeys = [...PRESERVED_KEYS];

  return {
    sessionStorageKeys,
    localStorageKeys,
    preservedKeys,
  };
};
